import PathNames from "@app/const/PathNames"
import MenyLifecycleEvent from "@app/events/MenyLifecycleEvent"
import MenyNativeEvent from "@app/events/MenyNativeEvent"
import { mapNativeLinkToRoute } from "@app/helpers"
import { hideHandoverPickerPopup, showCouponPopup } from "@app/store/app/appActions"
import { showRecipePopup } from "@app/store/app/appCreator"
import * as tracking from "@app/tracking"
import { getCampaignById } from "@modules/bundles/helpers/getCampaignById"
import { fetchRecipe } from "@modules/recipes/fetchRecipe"
import { BestBuyHistoryEntry } from "@modules/savings/BestBuyTypes"
import { IStoredShoppingList } from "@ng-mw/platform-interfaces"
import { getModalManager } from "@ng-mw/reol/components/Modal/useModalManager"
import { DebugLogger, LifecycleEmitter, NativeEventBridge } from "@ng-mw/shared-react-components"
import { AnyTrackingData } from "@ng-mw/shared-react-components/src/data/tracking/trackEvent"
import type { MemoryHistory } from "history"
import { sourceOptions } from "./source-options"

// This is just type-nonsense to convince typescript that dataLayer is a valid attribute of window
// (pretty stupid, but that's typescript...)
declare global {
    interface Window {
        dataLayer: AnyTrackingData[]
    }
}

export const navigateToOptions = {
    ALLETRANSAKSJONER: "ALLETRANSAKSJONER",
    BESPARELSER: "BESPARELSER",
    BONUS: "BONUS",
    BONUSTOBANK: "BONUSTOBANK",
    BUTIKK: "BUTIKK",
    CARDS: "CARDS",
    GODEKJØP: "GODEKJØP",
    HJEM: "HJEM",
    KHI: "KHI",
    KIRKENS: "KIRKENS",
    KLIPPEKORT: "KLIPPEKORT",
    KLIMA: "KLIMA",
    KNALL: "KNALL",
    KUPONGER: "KUPONGER",
    KURV: "KURV",
    MATSENTRALEN: "MATSENTRALEN",
    NETTHANDELHISTORIE: "NETTHANDELHISTORIE",
    TILBUD: "TILBUD",
    TVAKSJONEN: "TVAKSJONEN",
    UKEMENY: "UKEMENY",
    OPPSKRIFTER: "OPPSKRIFTER",
    LAGREDEOPPSKRIFTER: "LAGREDEOPPSKRIFTER",
    KJØPTEOPPSKRIFTER: "KJØPTEOPPSKRIFTER",
    BONUSKAMPANJER: "BONUSKAMPANJER",

    // Old/Bad/Misleading names
    MENYMER: "MENYMER", // alias for BONUSKAMPANJER
    OPPDAG: "OPPDAG", // alias for HJEM

    OPPSKRIFT: "OPPSKRIFT",
    BUNDLE: "BUNDLE",
}

// Using `store: Store` just gives this error:
// "Argument of type '(dispatcher: any) => void' is not assignable to parameter of type 'AnyAction'."
const historyConnectors = (store: any, history: MemoryHistory): void => {
    LifecycleEmitter.on(MenyLifecycleEvent.GoToContactDetails, async () => {
        store.dispatch(hideHandoverPickerPopup())
        history.push(PathNames.ContactDetails)
    })
    interface NavigateToEventData {
        navigateTo: string
        source?:
            | typeof sourceOptions.PUSH
            | typeof sourceOptions.LINK
            | typeof sourceOptions.TAB_BAR
    }

    NativeEventBridge.listen(
        MenyNativeEvent.NavigateTo,
        async ({ navigateTo, source }: NavigateToEventData) => {
            if (!navigateTo) {
                return // don't bother checking anything if there is no navigateTo argument
            }
            DebugLogger.log(`Got push with navigateTo: ${navigateTo}. Source: ${source}`)

            if (source === sourceOptions.TAB_BAR) {
                switch (navigateTo) {
                    case navigateToOptions.HJEM:
                        history.replace(PathNames.Home, { isImmediate: true })
                        return

                    case navigateToOptions.BUTIKK:
                        history.replace(PathNames.Search, { isImmediate: true })
                        return

                    case navigateToOptions.KURV:
                        history.replace(PathNames.Cart, { isImmediate: true })
                        return

                    case navigateToOptions.GODEKJØP:
                        history.replace(PathNames.BestBuy, { isImmediate: true })
                        return

                    case navigateToOptions.OPPSKRIFTER:
                        history.replace(PathNames.RecipesHome, { isImmediate: true })
                        return

                    default:
                        return
                }
            }

            // link - associated domains: https://developer.apple.com/documentation/xcode/supporting-associated-domains.
            // Push - native sends us a string with the name of where the user should be sent

            // If source = link: user has clicked on a link we want to open in the app. Else navigateTo comes from push
            if (source === sourceOptions.LINK) {
                const pathOrHistoryObject = mapNativeLinkToRoute(navigateTo)
                tracking.nativeAppLink({ navigateTo, source })
                history.push(pathOrHistoryObject)
            } else {
                const parts = navigateTo.split("/")
                const [navigateToTarget, navigateToId = "", rawCampaignNameOverride = ""] = parts
                const maxParts = navigateToTarget === navigateToOptions.BUNDLE ? 3 : 2

                if (parts.length > maxParts) {
                    DebugLogger.warn("Got invalid navigateTo argument count:", navigateTo)
                    return
                }

                switch (navigateToTarget) {
                    case navigateToOptions.TILBUD:
                        tracking.pushNotification(navigateTo)
                        history.push({
                            pathname: PathNames.BestBuy,
                            state: { initialFacet: "Tilbud" },
                        } as BestBuyHistoryEntry)
                        break
                    case navigateToOptions.GODEKJØP:
                        tracking.pushNotification(navigateTo)
                        history.push({
                            pathname: PathNames.BestBuy,
                        } as BestBuyHistoryEntry)
                        break
                    case navigateToOptions.KNALL:
                        tracking.pushNotification(navigateTo)
                        history.push({
                            pathname: PathNames.BestBuy,
                            state: { initialFacet: "Knallkjøp" },
                        } as BestBuyHistoryEntry)
                        break
                    case navigateToOptions.UKEMENY:
                        tracking.pushNotification(navigateTo)
                        history.push(PathNames.RecipesHome)
                        break
                    case navigateToOptions.BUTIKK:
                        tracking.pushNotification(navigateTo)
                        history.push(PathNames.Search)
                        break
                    case navigateToOptions.KHI:
                        tracking.pushNotification(navigateTo)
                        history.push(PathNames.DietHome)
                        break
                    case navigateToOptions.KLIMA:
                        tracking.pushNotification(navigateTo)
                        history.push(PathNames.ClimateHome)
                        break
                    case navigateToOptions.OPPDAG:
                        tracking.pushNotification(navigateTo)
                        history.push(PathNames.Home)
                        break
                    case navigateToOptions.CARDS:
                        tracking.pushNotification(navigateTo)
                        history.push(PathNames.TrumfBankCards)
                        break
                    case navigateToOptions.KURV:
                        tracking.pushNotification(navigateTo)
                        history.push(PathNames.Cart)
                        break
                    case navigateToOptions.NETTHANDELHISTORIE:
                        tracking.pushNotification(navigateTo)
                        history.push(PathNames.OrderHistory)
                        break
                    case navigateToOptions.ALLETRANSAKSJONER:
                        tracking.pushNotification(navigateTo)
                        history.push({
                            pathname: PathNames.PurchaseHistory,
                            state: { tabIndex: 1 },
                        })
                        break
                    case navigateToOptions.HJEM:
                        tracking.pushNotification(navigateTo)
                        history.push(PathNames.Home)
                        break
                    case navigateToOptions.MENYMER:
                    case navigateToOptions.BONUSKAMPANJER:
                        tracking.pushNotification(navigateTo)
                        history.push({
                            pathname: PathNames.BestBuy,
                            state: { initialFacet: "Bonuskampanjer" },
                        } as BestBuyHistoryEntry)
                        break
                    case navigateToOptions.KLIPPEKORT:
                        tracking.pushNotification(navigateTo)
                        history.push({
                            pathname: PathNames.UseCoupons,
                            state: { scrollTo: "punchCards" },
                        })
                        break
                    case navigateToOptions.KUPONGER:
                        tracking.pushNotification(navigateTo)
                        history.push({
                            pathname: PathNames.BestBuy,
                            state: { initialFacet: "Kuponger" },
                        } as BestBuyHistoryEntry)
                        break
                    case navigateToOptions.BESPARELSER:
                        tracking.pushNotification(navigateTo)
                        history.push({
                            pathname: PathNames.Savings,
                            state: { tabIndex: 0, forceRefresh: true },
                        })
                        break
                    case navigateToOptions.BONUS:
                        tracking.pushNotification(navigateTo)
                        history.push(PathNames.UseBonus)
                        break
                    case navigateToOptions.BONUSTOBANK:
                        tracking.pushNotification(navigateTo)
                        history.push(PathNames.UseBonusToBank)
                        break
                    case navigateToOptions.TVAKSJONEN:
                        tracking.pushNotification(navigateTo)
                        history.push({
                            pathname: PathNames.UseBonusToCharity,
                            state: { tabIndex: "TVAK" },
                        })
                        break
                    case navigateToOptions.MATSENTRALEN:
                        tracking.pushNotification(navigateTo)
                        history.push({
                            pathname: PathNames.UseBonusToCharity,
                            state: { tabIndex: "MATS" },
                        })
                        break
                    case navigateToOptions.KIRKENS:
                        tracking.pushNotification(navigateTo)
                        history.push({
                            pathname: PathNames.UseBonusToCharity,
                            state: { tabIndex: "KIRK" },
                        })
                        break
                    case navigateToOptions.OPPSKRIFTER:
                        tracking.pushNotification(navigateTo)
                        history.push(PathNames.RecipesHome)
                        break
                    case navigateToOptions.LAGREDEOPPSKRIFTER:
                        tracking.pushNotification(navigateTo)
                        history.push(PathNames.MyRecipes)
                        break
                    case navigateToOptions.KJØPTEOPPSKRIFTER:
                        tracking.pushNotification(navigateTo)
                        history.push({
                            pathname: PathNames.MyRecipes,
                            state: { data: { source: "purchased" } },
                        })
                        break
                    case navigateToOptions.OPPSKRIFT: {
                        if (!navigateToId) {
                            DebugLogger.warn("Got navigateTo target without id:", navigateTo)
                            history.push(PathNames.Home)
                            break
                        }

                        history.push(PathNames.RecipesHome)

                        const recipe = await fetchRecipe(navigateToId)

                        if (recipe) {
                            tracking.pushNotification(`${navigateToTarget}/${recipe.name}`)
                            LifecycleEmitter.broadcast(MenyLifecycleEvent.GoToRecipe, {
                                recipeId: recipe.id,
                                source: "push",
                            })
                        }

                        break
                    }
                    case navigateToOptions.BUNDLE: {
                        const storeId = (store.getState()?.user?.data?.store?.gln as string) ?? ""
                        const campaignId = Number(navigateToId)
                        const campaignNameOverride = rawCampaignNameOverride
                            .replace(/_/g, " ")
                            .trim()

                        if (!navigateToId || !storeId || !Number.isFinite(campaignId)) {
                            DebugLogger.warn(
                                !navigateToId
                                    ? "Got navigateTo target without id:"
                                    : !storeId
                                    ? "Missing storeId (gln) in redux store"
                                    : "Invalid campaignId in navigateToId",
                                !navigateToId ? navigateTo : { navigateToId, navigateTo },
                            )
                            history.push(PathNames.Home)
                            break
                        }

                        const to = `/savings/campaigns/campaign-${navigateToId}`
                        const isCurrentCampaignPage = history.location.pathname === to
                        const currentState = isCurrentCampaignPage
                            ? (history.location.state as any) ?? {}
                            : {}

                        let campaignName =
                            campaignNameOverride || currentState.campaignName || "Bundle"

                        tracking.pushNotification(`${navigateToTarget}/${navigateToId}`)

                        if (!campaignNameOverride) {
                            try {
                                const campaign = await getCampaignById(storeId, campaignId)
                                const resolvedCampaignName = campaign?.marketTextLong?.trim()

                                if (resolvedCampaignName) {
                                    campaignName = resolvedCampaignName
                                }
                            } catch (err: unknown) {
                                DebugLogger.warn("BUNDLE: could not resolve campaign name", err, {
                                    navigateToId,
                                    navigateTo,
                                })
                            }
                        }

                        const nextState = {
                            ...currentState,
                            campaignName,
                            campaignDescription: currentState.campaignDescription ?? "",
                            id: currentState.id ?? [navigateToId],
                            from: currentState.from ?? history.location.pathname,
                            isImmediate: currentState.isImmediate ?? false,
                        }

                        if (isCurrentCampaignPage) {
                            history.replace(to, nextState)
                        } else {
                            history.push(to, nextState)
                        }

                        break
                    }
                    case "":
                        tracking.pushNotification("(do nothing)")
                        DebugLogger.log("navigateTo empty string, do nothing")
                        break
                    default:
                        tracking.pushNotification(`Error unknown navigateTo target: ${navigateTo}`)
                        DebugLogger.warn("Got unknown navigateTo argument:", navigateTo)
                }
            }
        },
        false,
    ) // false is very important; it makes it listen more than once
    DebugLogger.log("Load-order-debug: Listening for MenyNativeEvent.NavigateTo IMPORTANT")

    LifecycleEmitter.on(MenyLifecycleEvent.GoToSavingsClicked, (state: any) => {
        history.push({ pathname: PathNames.Savings, state })
    })

    LifecycleEmitter.on(MenyLifecycleEvent.GoToShoppinglistClicked, () => {
        history.push(PathNames.ShoppingListsHome)
    })

    LifecycleEmitter.on(MenyLifecycleEvent.GoToTransactionHistoryClicked, () => {
        history.push(PathNames.PurchaseHistory)
    })

    LifecycleEmitter.on(MenyLifecycleEvent.GoToMyRecipesClicked, () => {
        history.push({
            pathname: PathNames.RecipesHome,
            state: {
                initialTab: 1,
            },
        })
    })

    LifecycleEmitter.on(MenyLifecycleEvent.GoToMyOrders, () => {
        history.push(PathNames.OrderHistory)
    })

    interface GoToOrderEventData {
        ngOrderId: string
        isArchived: boolean
        pickupCode: string
    }

    LifecycleEmitter.on(
        MenyLifecycleEvent.GoToOrder,
        ({ ngOrderId, isArchived, pickupCode }: GoToOrderEventData) => {
            history.push({
                pathname: `/user/orders/${ngOrderId}`,
                state: {
                    ngOrderId,
                    pickupCode,
                    isArchived,
                },
            })
        },
    )

    interface GoToRecipeEventData {
        recipeId: string
    }

    LifecycleEmitter.on(MenyLifecycleEvent.GoToRecipe, ({ recipeId }: GoToRecipeEventData) => {
        store.dispatch(showRecipePopup(recipeId))
    })

    LifecycleEmitter.on(MenyLifecycleEvent.GoToCouponsClicked, () => {
        if (history.location.pathname === PathNames.Benefits) {
            getModalManager().closeAllCloseableModals()
            // TODO somehow work with BestBuy page
        } else {
            store.dispatch(showCouponPopup())
        }
    })

    interface GoToShoppingListDetailsClickedEventData {
        id: string
        shoppingList: IStoredShoppingList
    }

    LifecycleEmitter.on(
        MenyLifecycleEvent.GoToShoppingListDetailsClicked,
        ({ id, shoppingList }: GoToShoppingListDetailsClickedEventData) => {
            history.push({
                pathname: `/shopping-lists/${id}`,
                state: {
                    id,
                    shoppingList,
                },
            })
        },
    )
}

export default historyConnectors
